<template>
  <div class="page-analytics">
    <h1 class="title">Analytics</h1>

    <!-- Concepts -->
    <section>
      <h2 class="subtitle">Analytical Concepts</h2>
      <button class="button is-primary mb-3" @click="showConceptModal = true">
        + Add Concept
      </button>

      <table class="table is-fullwidth">
        <thead>
          <tr>
            <th>Name</th>
            <th>Scope</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="concept in concepts" :key="concept.id">
            <td>{{ concept.name }}</td>
            <td>{{ concept.scope_label }}</td>
            <td>
              <button
                class="button is-small is-info"
                @click="selectConcept(concept)"
              >
                View Codes
              </button>
              <button
                class="button is-small is-light"
                @click="editConcept(concept)"
              >
                Edit
              </button>
              <button
                class="button is-small is-danger"
                @click="deleteConcept(concept)"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- Codes -->
    <section v-if="selectedConcept">
      <h2 class="subtitle">Codes for {{ selectedConcept.name }}</h2>
      <button class="button is-primary mb-3" @click="addCode">
        + Add Code
      </button>

      <table class="table is-fullwidth">
        <thead>
          <tr>
            <th>Label</th>
            <th>Description</th>
            <th>Active</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="code in codes" :key="code.id">
            <td>{{ code.label }}</td>
            <td>{{ code.description }}</td>
            <td>
              <span v-if="code.is_active" class="tag is-success">Active</span>
              <span v-else class="tag is-light">Inactive</span>
            </td>
            <td>
              <button class="button is-small is-light" @click="editCode(code)">
                Edit
              </button>
              <button
                class="button is-small is-danger"
                @click="deleteCode(code)"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- Modals -->
    <ConceptFormModal
      :visible="showConceptModal"
      :concept="editingConcept"
      @close="closeConceptModal"
      @saved="fetchConcepts"
    />

    <CodeFormModal
      v-if="selectedConcept"
      :visible="showCodeModal"
      :code="editingCode"
      :concept-id="selectedConcept.id"
      @close="closeCodeModal"
      @saved="fetchCodes(selectedConcept.id)"
    />
  </div>
</template>

<script>
import api from '@/utils/axios';
import ConceptFormModal from '@/components/ConceptFormModal.vue';
import CodeFormModal from '@/components/CodeFormModal.vue';
import { toast } from 'bulma-toast';
export default {
  name: 'AnalyticsView',
  components: { ConceptFormModal, CodeFormModal },
  data() {
    return {
      concepts: [],
      selectedConcept: null,
      codes: [],
      showConceptModal: false,
      editingConcept: null,
      showCodeModal: false,
      editingCode: null,
    };
  },
  created() {
    this.fetchConcepts();
  },
  methods: {
    async fetchConcepts() {
      const res = await api.get('/analytics/concepts/');
      this.concepts = res.data;
    },
    async fetchCodes(conceptId) {
      const res = await api.get('/analytics/codes/', {
        params: { concept: conceptId },
      });
      this.codes = res.data;
    },
    selectConcept(concept) {
      this.selectedConcept = concept;
      this.fetchCodes(concept.id);
    },
    editConcept(concept) {
      this.editingConcept = concept;
      this.showConceptModal = true;
    },
    closeConceptModal() {
      this.showConceptModal = false;
      this.editingConcept = null;
    },
    editCode(code) {
      this.editingCode = code;
      this.showCodeModal = true;
    },
    closeCodeModal() {
      this.showCodeModal = false;
      this.editingCode = null;
    },
    addCode() {
      this.editingCode = null;
      this.showCodeModal = true;
    },
    async deleteConcept(concept) {
      if (
        !confirm(
          `Delete concept "${concept.name}"? This will delete all codes.`
        )
      ) {
        return;
      }

      try {
        await api.delete(`/analytics/concepts/${concept.id}/`);

        // refresh list
        this.fetchConcepts();

        // reset selection if needed
        if (this.selectedConcept?.id === concept.id) {
          this.selectedConcept = null;
          this.codes = [];
        }
      } catch (e) {
        alert('Error deleting concept');
      }
    },
    async deleteCode(code) {
      if (!confirm(`Delete code "${code.label}"?`)) {
        return;
      }

      try {
        await api.delete(`/analytics/codes/${code.id}/`);

        toast({
          message: 'Code deleted',
          type: 'is-success',
        });

        this.fetchCodes(this.selectedConcept.id);
      } catch (e) {
        toast({
          message: 'Error deleting code',
          type: 'is-danger',
        });
      }
    },
  },
};
</script>
